import React from "react";
import {
  Navigation,
  Page,
  TaskSorting,
  TaskSelect,
  TaskImageClick,
  Figure,
  Image,
  Storage,
} from "@bittnerdevelopmentgroup/interactive";

import "./styles.css";

import panel1a from "./assets/PrinEco5_ProfitMaximization_p1a.jpg";
import panel1b from "./assets/PrinEco5_ProfitMaximization_p1b.jpg";
import panel1c from "./assets/PrinEco5_ProfitMaximization_p1c.jpg";
import panel3 from "./assets/PrinEco5_ProfitMaximization_p3.jpg";

class ProfitMaximization extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className="profitMaximization">
        <div className="sr-only" aria-hidden="true">
          {[panel1a, panel1b, panel1c, panel3].map(src => <img alt="" src={src} />)}
        </div>
        <Navigation interactiveId="chapter-9a">
          <Page>
            <p>
              Your friend Yesi is an entrepreneur and is always trying to maximize the profits of her businesses. She is wondering if she should make more of some of her best-selling products: hats, customized sneakers, and custom copper bracelets. She asks for your advice about which products she should make more of and which she shouldn't.
            </p>
            <p>
              Assuming that Yesi has plenty of time to work on all three projects, they all would take about the same amount of time and effort. <b>What would you recommend that she do in each of the following situations?</b>
            </p>
            <i>
              Read each situation and then decide whether Yesi should produce an additional unit of the product or stop producing. Assume that Yesi has enough time to make as many of the following as she'd like.
            </i>
            <TaskSorting
              state={Storage()}
              feedbackCorrect="You got it! When Yesi's revenue from producing an additional unit is greater than the cost, she should produce one more. When the cost of producing an additional unit is greater than the revenue, she should not produce one more. So, Yesi should make more hats and sneakers, but she should not make more bracelets."
              feedbackIncorrect={
                <span>
                  Remember to focus on the <em>additional</em> (marginal) revenue and <em>additional</em> (marginal) cost of making one more unit of each product.
                  If the extra revenue from the next unit is greater than the extra cost,
                  she should make one more. If the extra cost is higher than the extra
                  revenue, she should not make one more.
                </span>
              }
              prompt={''}
              categories={[
                "Make one more",
                "Don't make one more"
              ]}
            >
              <TaskSorting.Option category={0}>
                <Image src={panel1a} alt="A photo of four knit hats." photoCredit={"Shutterstock / Remistudio"} />
                Yesi knits custom hats. She has already knit 20 this season. Another hat would cost her $10 in yarn and other supplies, and she could make $20 of revenue from it.
              </TaskSorting.Option>
              <TaskSorting.Option category={0}>
                <Image src={panel1b} alt="A photo of a white sneaker with flowers painted on it." photoCredit={"Shutterstock / Yuriy Golub"} />
                Yesi also customizes sneakers, and she could make $30 in revenue from painting another pair. It would cost her $5 to buy the paint to do so.
              </TaskSorting.Option>
              <TaskSorting.Option category={1}>
                <Image src={panel1c} alt="A photo of two delicate, identical copper bracelets." photoCredit={"Shutterstock / Selimstock"} />
                Yesi also makes custom copper bracelets. Because of the copper shortage, those supplies have gotten more expensive. It would cost her $38 to make another bracelet, and she would make $30 in revenue from it.
              </TaskSorting.Option>
            </TaskSorting>
          </Page>
          <Page>
            <p>
              In these situations, economists would advise Yesi to follow the <b>profit-maximizing rule</b>. The rule states that profit maximization occurs when a firm expands output as long as marginal revenue is greater than marginal cost.
            </p>
            <p>
              The
              <TaskSelect
                state={Storage()}
                options={["marginal revenue", "marginal cost",]}
                label={<span>The </span>}
                description={<span> from producing one more bracelet is the $30 Yesi would receive for selling it.</span>}
                correct={0}
                feedback={["The money Yesi receives from selling one more unit is marginal revenue.", "The $30 Yesi earns from selling one more bracelet does not represent marginal cost."]}
                srOnly
                inline
              /> from producing one more bracelet is the $30 Yesi would receive for selling it. The
              <TaskSelect
                state={Storage()}
                options={["marginal revenue", "marginal cost",]}
                label={<span>The </span>}
                description={<span> of producing that bracelet is the $38 she must spend on copper and other supplies.</span>}
                correct={1}
                feedback={["The $38 Yesi spends to produce one more bracelet does not represent marginal revenue.", "The $38 Yesi must spend to produce one more bracelet is the marginal cost."]}
                srOnly
                inline
              /> of producing that bracelet is the $38 she must spend on copper and other supplies.
            </p>
            <p>Since the marginal revenue of that additional unit ($30) is less than the marginal cost ($38), she shouldn't make the bracelet.</p>
          </Page>
          <Page>
            <p>
              Here's where it gets trickier: What if Yesi could sell the next bracelet for $38? Should she make that next unit?
            </p>
            <p>
              Economists say yes! The profit-maximizing rule states that profit maximization occurs when a firm stops as close to MR = MC as possible. Since the marginal revenue of that unit is $38, and the marginal cost is $38, that means that MR = MC. According to the profit-maximizing rule, Yesi should stop at that point <i>and not before</i>.
            </p>
            <p>
              <i>Select the point of profit maximization in the following graph.</i>
            </p>
            <TaskImageClick
              state={Storage()}
              hotspots={[
                {
                  correct: false,
                  label: "A point on line MR",
                  feedback: "At what point does MR = MC?",
                  style: {
                    top: "160px",
                    left: "212px",
                    width: "50px",
                    height: "50px",
                  },
                },
                {
                  correct: false,
                  label: "A point on line MC with high price and high quantitiy, where line MC intersects no other lines.",
                  feedback: "At what point does MR = MC?",
                  style: {
                    top: "75px",
                    left: "493px",
                    width: "50px",
                    height: "50px",
                  },
                },
                {
                  correct: true,
                  label: "Line MC intersecting line MR at point Q",
                  feedback: "Profit maximziation occurs at this point, where MR = MC.",
                  style: {
                    top: "160px",
                    left: "440px",
                    width: "50px",
                    height: "50px",
                  },
                },
              ]}
            >
              <Figure
                alt="Marginal cost and marginal revenue curves."
                longAlt={"A graph with quantity on the x axis and price and cost on the y axis. A marginal cost curve dips to a minimum point, then rises steadily. At cost $38 it crosses a horizontal marginal revenue curve. Three points are singled out by dots, namely a point on the marginal revenue curve that is directly above the start of the marginal cost curve, the point where the two curves cross, and a point higher up on the marginal cost curve."}
                src={panel3}
                width="80%"
              />
            </TaskImageClick>
          </Page>
        </Navigation>
      </div>
    )
  }
}

export default ProfitMaximization
